import { useState } from 'react'
import { CopyIcon } from '@/shared/ui/icons'

const SIZE_CLASSES = {
  xs: 'text-xs',
  sm: 'text-sm',
  default: 'text-sm',
} as const

const WEIGHT_CLASSES = {
  semibold: 'font-semibold',
  medium: 'font-medium',
} as const

export interface ErrorMessageWithCopyProps {
  message: string
  details?: string[]
  className?: string
  /** Classe CSS para o texto. Se informado, tem precedência sobre size/weight. */
  textClassName?: string
  /** Tamanho do texto (ignorado se textClassName informado) */
  size?: 'xs' | 'sm' | 'default'
  /** Peso da fonte (ignorado se textClassName informado) */
  weight?: 'semibold' | 'medium'
}

const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

export const ErrorMessageWithCopy = ({
  message,
  details = [],
  className = '',
  textClassName,
  size = 'default',
  weight = 'semibold',
}: ErrorMessageWithCopyProps) => {
  const resolvedTextClassName =
    textClassName ?? `${SIZE_CLASSES[size]} ${WEIGHT_CLASSES[weight]}`
  const [copied, setCopied] = useState(false)
  const fullLog = details.length ? [message, ...details].join('\n') : message

  const handleCopy = async () => {
    const ok = await copyToClipboard(fullLog)
    setCopied(ok)
    if (ok) setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className={`flex items-start gap-2 ${className}`}>
      <button
        type="button"
        onClick={handleCopy}
        className="shrink-0 flex items-center justify-center rounded p-1 text-[var(--color-wine-strong)] hover:bg-[var(--color-wine-soft-hover)] transition-colors"
        aria-label={copied ? 'Copiado' : 'Copiar log do erro'}
        title={copied ? 'Copiado' : 'Copiar log do erro'}
      >
        <CopyIcon size={18} />
      </button>
      <div className="flex-1 min-w-0">
        <p className={`whitespace-pre-line ${resolvedTextClassName} text-[var(--color-wine-on-soft)]`}>
          {message}
        </p>
        {details.length ? (
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-[var(--color-text-secondary)]">
            {details.slice(0, 8).map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
